<template>
    <view class="forgot-password-container">
        <view class="forgot-password-box">
            <!-- 头部 -->
            <view class="forgot-password-header">
                <text class="title">找回密码</text>
                <text class="subtitle">通过邮箱验证码重置您的密码</text>
            </view>

            <!-- 步骤指示器 -->
            <view class="step-indicator">
                <view class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                    <view class="step-number">1</view>
                    <text class="step-text">输入邮箱/用户名</text>
                </view>
                <view class="step-line" :class="{ active: currentStep > 1 }"></view>
                <view class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                    <view class="step-number">2</view>
                    <text class="step-text">验证码验证</text>
                </view>
                <view class="step-line" :class="{ active: currentStep > 2 }"></view>
                <view class="step" :class="{ active: currentStep >= 3 }">
                    <view class="step-number">3</view>
                    <text class="step-text">重置密码</text>
                </view>
            </view>

            <!-- 步骤1：输入邮箱/用户名 -->
            <view v-if="currentStep === 1" class="step-content">
                <view class="forgot-form">
                    <view class="form-item">
                        <input
                                class="input"
                                type="text"
                                v-model.trim="emailForm.emailOrUsername"
                                placeholder="请输入邮箱地址或用户名（至少3位）"
                                @confirm="sendVerificationCode"
                        />
                    </view>

                    <button class="action-btn primary" :loading="sendingCode" @click="sendVerificationCode">
                        {{ sendingCode ? '发送中...' : '发送验证码' }}
                    </button>
                </view>
            </view>

            <!-- 步骤2：验证码验证 -->
            <view v-if="currentStep === 2" class="step-content">
                <view class="info-tip info-blue">
                    <text class="icon">📧</text>
                    <text>验证码已发送至：{{ targetEmail || '您的绑定邮箱' }}</text>
                </view>

                <view class="forgot-form">
                    <view class="form-item">
                        <input
                                class="input"
                                type="number"
                                v-model.trim="codeForm.code"
                                maxlength="6"
                                placeholder="请输入6位验证码"
                                @confirm="verifyCode"
                        />
                    </view>

                    <view class="code-actions">
                        <button class="action-btn primary" :loading="verifyingCode" @click="verifyCode">
                            {{ verifyingCode ? '验证中...' : '验证码验证' }}
                        </button>

                        <button class="text-btn" :disabled="countdown > 0" @click="resendCode">
                            {{ countdown > 0 ? countdown + '秒后重发' : '重新发送' }}
                        </button>
                    </view>
                </view>
            </view>

            <!-- 步骤3：重置密码 -->
            <view v-if="currentStep === 3" class="step-content">
                <view class="info-tip info-green">
                    <text class="icon">✅</text>
                    <text>验证码验证成功</text>
                </view>

                <view class="forgot-form">
                    <view class="form-item">
                        <input
                                class="input"
                                type="password"
                                v-model.trim="passwordForm.newPassword"
                                placeholder="请输入新密码（至少6位）"
                        />
                    </view>

                    <view class="form-item">
                        <input
                                class="input"
                                type="password"
                                v-model.trim="passwordForm.confirmPassword"
                                placeholder="请再次输入新密码"
                                @confirm="resetPasswordSubmit"
                        />
                    </view>

                    <button class="action-btn primary" :loading="resettingPassword" @click="resetPasswordSubmit">
                        {{ resettingPassword ? '重置中...' : '重置密码' }}
                    </button>
                </view>
            </view>

            <!-- 返回登录 -->
            <view class="back-to-login">
                <text class="back-link" @click="goBackToLogin">← 返回登录</text>
            </view>
        </view>
    </view>
</template>

<script>
import { sendPasswordResetCode, verifyPasswordResetCode, resetPassword } from '@/api/password'

export default {
    name: 'ForgotPassword',
    data() {
        return {
            currentStep: 1,
            sendingCode: false,
            verifyingCode: false,
            resettingPassword: false,
            countdown: 0,
            countdownTimer: null,

            emailForm: { emailOrUsername: '' },
            targetEmail: '',

            codeForm: { code: '' },

            passwordForm: {
                newPassword: '',
                confirmPassword: ''
            }
        }
    },

    onUnload() {
        this.clearCountdown()
    },

    methods: {
        // ======= 基础校验 =======
        validateAccount(v) {
            return typeof v === 'string' && v.trim().length >= 3
        },
        validateCode(v) {
            return /^\d{6}$/.test(v || '')
        },
        validatePassword(v) {
            return typeof v === 'string' && v.length >= 6
        },
        validateConfirmPassword() {
            if (!this.validatePassword(this.passwordForm.newPassword)) {
                this.$message.warning('新密码至少6位')
                return false
            }
            if (this.passwordForm.confirmPassword !== this.passwordForm.newPassword) {
                this.$message.warning('两次输入的密码不一致')
                return false
            }
            return true
        },

        // ======= 步骤1：发送验证码 =======
        async sendVerificationCode() {
            const account = this.emailForm.emailOrUsername
            if (!this.validateAccount(account)) {
                return this.$message.warning('请输入正确的邮箱或用户名（≥3位）')
            }

            this.sendingCode = true
            try {
                const resp = await sendPasswordResetCode(account)
                // 若后端返回 target email，则显示使用
                this.targetEmail = resp?.data?.email || (account.includes('@') ? account : '您绑定的邮箱')
                this.$message.success('验证码已发送，请查收')
                this.currentStep = 2
                this.startCountdown()
            } catch (e) {
                // 全局拦截器会统一提示
            } finally {
                this.sendingCode = false
            }
        },

        // ======= 步骤2：验证码验证 =======
        async verifyCode() {
            const account = this.emailForm.emailOrUsername
            if (!this.validateAccount(account)) {
                return this.$message.warning('请输入正确的邮箱或用户名')
            }
            if (!this.validateCode(this.codeForm.code)) {
                return this.$message.warning('验证码需为6位数字')
            }

            this.verifyingCode = true
            try {
                await verifyPasswordResetCode(account, this.codeForm.code)
                this.$message.success('验证码验证成功')
                this.currentStep = 3
            } catch (e) {
                // 全局拦截器会统一提示
            } finally {
                this.verifyingCode = false
            }
        },

        // 重新发送验证码
        async resendCode() {
            if (this.countdown > 0) return
            const account = this.emailForm.emailOrUsername
            if (!this.validateAccount(account)) {
                return this.$message.warning('请先输入邮箱或用户名')
            }

            this.sendingCode = true
            try {
                const resp = await sendPasswordResetCode(account)
                this.targetEmail = resp?.data?.email || this.targetEmail
                this.$message.success('验证码已重新发送')
                this.startCountdown()
            } catch (e) {
                // 全局拦截器会统一提示
            } finally {
                this.sendingCode = false
            }
        },

        // ======= 步骤3：重置密码 =======
        async resetPasswordSubmit() {
            if (!this.validatePassword(this.passwordForm.newPassword)) {
                return this.$message.warning('新密码至少6位')
            }
            if (!this.validateConfirmPassword()) return

            this.resettingPassword = true
            try {
                await resetPassword(
                    this.emailForm.emailOrUsername,
                    this.codeForm.code,
                    this.passwordForm.newPassword,
                    this.passwordForm.confirmPassword
                )
                this.$message.success('密码重置成功，请使用新密码登录')
                setTimeout(() => this.goBackToLogin(), 1000)
            } catch (e) {
                // 全局拦截器会统一提示
            } finally {
                this.resettingPassword = false
            }
        },

        // ======= 倒计时 =======
        startCountdown() {
            this.clearCountdown()
            this.countdown = 60
            this.countdownTimer = setInterval(() => {
                this.countdown--
                if (this.countdown <= 0) {
                    this.clearCountdown()
                }
            }, 1000)
        },
        clearCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
            this.countdown = 0
        },

        // ======= 返回登录页 =======
        goBackToLogin() {
            this.$router.push('/login')
        }
    }
}
</script>

<style scoped>
.forgot-password-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #66eae3 0%, #ffc7ff 100%);
    padding: 40rpx;
}

.forgot-password-box {
    width: 100%;
    max-width: 700rpx;
    padding: 60rpx;
    background: #ffffff;
    border-radius: 24rpx;
    box-shadow: 0 20rpx 40rpx rgba(0, 0, 0, 0.08);
}

.forgot-password-header {
    text-align: center;
    margin-bottom: 60rpx;
}

.title {
    color: #333;
    margin-bottom: 12rpx;
    font-size: 36rpx;
    font-weight: 700;
}

.subtitle {
    color: #666;
    font-size: 24rpx;
}

/* 步骤指示器 */
.step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 60rpx;
    padding: 0 20rpx;
}

.step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
}

.step-number {
    width: 64rpx;
    height: 64rpx;
    border-radius: 50%;
    background: #e0e0e0;
    color: #999;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 28rpx;
    transition: all 0.3s ease;
}

.step.active .step-number {
    background: #409eff;
    color: #fff;
}

.step.completed .step-number {
    background: #67c23a;
    color: #fff;
}

.step-text {
    margin-top: 8rpx;
    font-size: 22rpx;
    color: #999;
    text-align: center;
}

.step.active .step-text {
    color: #409eff;
    font-weight: 600;
}

.step.completed .step-text {
    color: #67c23a;
}

.step-line {
    width: 100rpx;
    height: 4rpx;
    background: #e0e0e0;
    margin: 0 16rpx;
    transition: all 0.3s ease;
}

.step-line.active {
    background: #409eff;
}

/* 步骤内容/表单 */
.step-content {
    margin-bottom: 40rpx;
}

.forgot-form {
    margin-top: 24rpx;
}

.form-item {
    margin-bottom: 24rpx;
}

.input {
    width: 100%;
    height: 88rpx;
    border-radius: 16rpx;
    padding: 0 24rpx;
    background: #f8f9fa;
    border: 2rpx solid #eaeaea;
    font-size: 28rpx;
    color: #333;
}

.input:focus {
    border-color: #409eff;
}

/* 按钮 */
.action-btn {
    width: 100%;
    height: 92rpx;
    font-size: 30rpx;
    border-radius: 16rpx;
    margin-top: 20rpx;
    border: none;
}

.primary {
    background: #409eff;
    color: #fff;
}

.primary:active {
    opacity: 0.95;
    transform: translateY(2rpx);
}

/* 信息提示 */
.info-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24rpx;
    border-radius: 16rpx;
    margin-bottom: 24rpx;
    font-size: 26rpx;
}

.info-blue {
    background: #f0f9ff;
    border: 2rpx solid #b3d8ff;
    color: #409eff;
}

.info-green {
    background: #f0fff5;
    border: 2rpx solid #bff0c9;
    color: #67c23a;
}

.icon {
    margin-right: 12rpx;
    font-size: 30rpx;
}

/* 验证码区域 */
.code-actions {
    display: flex;
    flex-direction: column;
    gap: 24rpx;
}

.text-btn {
    background: transparent;
    color: #666;
    font-size: 26rpx;
    border: none;
}

.text-btn:disabled {
    color: #bbb;
}

/* 返回登录 */
.back-to-login {
    text-align: center;
    margin-top: 40rpx;
}

.back-link {
    color: #666;
    font-size: 26rpx;
}
</style>
